import React, { useState, useEffect, useRef } from 'react';
// import './PortfoliosSection.css';

const PortfoliosSection = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const isDragging = useRef(false);
    const startY = useRef(0);
    const currentTranslateY = useRef(0);
    const containerRef = useRef(null);
    const scrollCooldown = useRef(false);
    const autoPlayRef = useRef(null);

    const slides = [
        {
            title: "Fashion Photography",
            tags: ["Fashion", "Photography", "Commercial"],
            bg: "/portfolios/fashion.jpg"
        },
        {
            title: "Acting Photography",
            tags: ["Acting", "Headshots", "Cinematic"],
            bg: "/portfolios/acting.jpg"
        },
        {
            title: "Wedding Photography",
            tags: ["Wedding", "Candid", "Vignettes"],
            bg: "/portfolios/wedding.jpg"
        },
        {
            title: "Product Photography",
            tags: ["Product", "Studio", "Commercial"],
            bg: "/portfolios/product.jpg"
        },
        {
            title: "Child Photography",
            tags: ["Child Portraiture", "Natural Light"],
            bg: "/portfolios/child.jpg"
        },
        {
            title: "Interior Photography",
            tags: ["Interior", "Architecture", "Design"],
            bg: "/portfolios/interior.jpg"
        },
        {
            title: "Corporate Photography",
            tags: ["Corporate", "Enterprise", "Profiles"],
            bg: "/portfolios/corporate.jpg"
        }
    ];

    const goToSlide = (index) => {
        setCurrentIndex(index);
        scrollCooldown.current = true;
        setTimeout(() => {
            scrollCooldown.current = false;
        }, 1000);
    };

    const handleNext = () => {
        goToSlide((currentIndex + 1) % slides.length);
    };

    const startAutoPlay = () => {
        stopAutoPlay();
        autoPlayRef.current = setInterval(() => {
            handleNext();
        }, 5000);
    };

    const stopAutoPlay = () => {
        if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };

    useEffect(() => {
        const handleWheel = (e) => {
            e.preventDefault();
            if (scrollCooldown.current) return;

            if (e.deltaY > 15 && currentIndex < slides.length - 1) {
                goToSlide(currentIndex + 1);
            } else if (e.deltaY < -15 && currentIndex > 0) {
                goToSlide(currentIndex - 1);
            }
        };

        const container = containerRef.current;
        if (container) {
            container.addEventListener('wheel', handleWheel, { passive: false });
        }
        startAutoPlay();

        return () => {
            if (container) container.removeEventListener('wheel', handleWheel);
            stopAutoPlay();
        };
    }, [currentIndex]);

    // Mouse Down (Drag Start)
    const handleMouseDown = (e) => {
        isDragging.current = true;
        startY.current = e.clientY;
        stopAutoPlay();
        if (containerRef.current) containerRef.current.style.cursor = 'grabbing';
    };

    // Mouse Move (Dragging Action)
    const handleMouseMove = (e) => {
        if (!isDragging.current) return;
        currentTranslateY.current = e.clientY - startY.current;
    };

    // Mouse Up (Drag Release)
    const handleMouseUp = () => {
        if (!isDragging.current) return;
        isDragging.current = false;
        if (containerRef.current) containerRef.current.style.cursor = 'grab';

        if (currentTranslateY.current < -70 && currentIndex < slides.length - 1) {
            goToSlide(currentIndex + 1);
        } else if (currentTranslateY.current > 70 && currentIndex > 0) {
            goToSlide(currentIndex - 1);
        }
        currentTranslateY.current = 0;
        startAutoPlay();
    };

    const renderGranularRuler = () => {
        return (
            <div className="ruler-track" style={{ transform: `translateX(calc(50% - ${currentIndex * 160}px - 80px))` }}>
                {slides.map((_, i) => (
                    <div key={i} className={`ruler-segment ${i === currentIndex ? 'active' : ''}`}>
                        <span className="tick-number">{String(i + 1).padStart(2, '0')}</span>
                        <div className="sub-ticks-cluster">
                            <div className="tick-line major"></div>
                            <div className="tick-line minor"></div>
                            <div className="tick-line minor"></div>
                            <div className="tick-line minor"></div>
                            <div className="tick-line minor"></div>
                            <div className="tick-line mid"></div>
                            <div className="tick-line minor"></div>
                            <div className="tick-line minor"></div>
                            <div className="tick-line minor"></div>
                            <div className="tick-line minor"></div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <section 
            className="parallax-showcase-viewport portfolios-section"
            ref={containerRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
        >
            {/* Top Fine-Grained Focus Meter Ruler */}
            <div className="ruler-bar fine-ruler-top">
                {renderGranularRuler()}
            </div>

            {/* Canvas Slides Assembly Box */}
            <div className="slides-container">
                {slides.map((slide, index) => {
                    let stateClass = "next-slide";
                    if (index === currentIndex) stateClass = "active-slide";
                    if (index < currentIndex) stateClass = "prev-slide";

                    return (
                        <div key={index} className={`parallax-slide ${stateClass}`}>
                            <div 
                                className="slide-background-parallax"
                                style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.25), rgba(0,0,0,0.5)), url(${slide.bg})` }}
                            />
                            
                            <div className="slide-interface-overlay">
                                <div className="info-content-left">
                                    <h2 className="slide-main-title">{slide.title}</h2>
                                    <div className="tag-pills-row">
                                        {slide.tags.map((tag, tIdx) => (
                                            <span key={tIdx} className="tag-pill-badge">{tag}</span>
                                        ))}
                                    </div>
                                </div>
                                
                                <div className="info-content-right">
                                    <button className="btn-view-details-circle">
                                        <span>View Details</span>
                                        <div className="arrow-icon-circle">
                                            <i className="fa-solid fa-camera-retro lens-aperture-icon"></i>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Bottom Fine-Grained Focus Meter Ruler */}
            <div className="ruler-bar fine-ruler-bottom">
                {renderGranularRuler()}
            </div>

            {/* Clean Back To Top Mini Pill Indicator */}
            <div className="fixed-bottom-indicator">
                <i className="fa-solid fa-arrow-up"></i>
            </div>
        </section>
    );
};

export default PortfoliosSection;